import "@/assets/main.css";
import "tailwindcss";
import PrimeVue from "primevue/config";

import I18nOptions from "@/configuration/i18n";
import { createI18n } from "vue-i18n";
import EnMessages from "@/i18n/en.json";
import DeMessages from "@/i18n/de.json";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";

import { createApp } from "vue";
import { createPinia } from "pinia";
import { useConfig } from "@/composables/config";
import "primeicons/primeicons.css";
import { useAuthStore } from "./stores/auth";
import { useSignalR } from "@/composables/signalr";
import { useSentry } from "@/composables/sentry";
import * as Sentry from "@sentry/vue";
const { initSignalR } = useSignalR();

const { config } = useConfig();

const i18n = createI18n({
  ...I18nOptions,
  legacy: false,
  messages: {
    en: EnMessages,
    de: DeMessages,
  },
});

import App from "./App.vue";
import router from "./router";
import { MyPreset } from "@/configuration/theme";
import { client } from "./client/client.gen.ts";

// Declare global variables for build-time constants
declare global {
  const __APP_VERSION__: string;
  const __GIT_HASH__: string;
  const __BUILD_TIME__: string;
}

const app = createApp(App);

// Generate release identifier
const releaseId = `oxly-frontend@${__APP_VERSION__}+${__GIT_HASH__}`;
if (!import.meta.env.DEV) {
  Sentry.init({
    app,
    dsn: config.value.SentryDsn,
    environment: config.value.SentryEnvironment,
    release: releaseId,
    sendDefaultPii: config.value.SentrySendDefaultPii === "true",
    tracesSampleRate: config.value.SentryTracesSampleRate,
    profilesSampleRate: config.value.SentryProfilesSampleRate,
    replaysSessionSampleRate: config.value.SentryReplaySessionSampleRate,
    replaysOnErrorSampleRate: config.value.SentryReplayOnErrorSampleRate,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: config.value.SentryEnvironment === "production",
        blockAllMedia: config.value.SentryEnvironment === "production",
      }),
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        showBranding: true,
        buttonLabel: "Report a Bug",
        submitButtonLabel: "Send Feedback",
        cancelButtonLabel: "Cancel",
        formTitle: "Bug Report",
        messagePlaceholder: "Describe the issue you encountered...",
        nameLabel: "Name (optional)",
        emailLabel: "Email (optional)",
        // Disable auto inject - we'll handle positioning manually
        autoInject: false,
        // Additional configuration
        showName: true,
        showEmail: true,
        isRequiredName: false,
        isRequiredEmail: false,
      }),
    ],
    beforeSend(event) {
      // Add build information to all events
      if (event.extra) {
        event.extra.buildTime = __BUILD_TIME__;
        event.extra.gitHash = __GIT_HASH__;
      } else {
        event.extra = {
          buildTime: __BUILD_TIME__,
          gitHash: __GIT_HASH__,
        };
      }
      return event;
    },
    tracePropagationTargets: [...config.value.SentryTracePropagationTargets],
    // Only enable debug in development
    debug: import.meta.env.DEV && false,
  });
}
const pinia = createPinia();
app.use(pinia);
app.use(ToastService);
app.use(ConfirmationService);
app.use(PrimeVue, {
  theme: {
    preset: MyPreset,
    ripple: true,
  },
  locale: {
    today: "Heute",
    clear: "Zurücksetzen",
  },
});
app.use(i18n);
app.use(router);

// Initialize auth before mounting
const authStore = useAuthStore(pinia);

// Initialize Sentry user context tracking
// Disable sentry if development mode
useSentry();

// Check if we're handling a callback - if so, don't initialize SignalR yet
const isCallback = window.location.pathname === "/callback";

client.setConfig({
  baseUrl: config.value.ApiBaseUrl,
  auth: () => {
    return authStore.getToken();
  },
});

authStore
  .init()
  .then(() => {
    // Only initialize SignalR if we're not in callback mode
    // (callback will redirect and reinitialize)
    if (!isCallback) {
      return initSignalR(config.value.SignalRUrl);
    }
  })
  .then(() => {
    // Only mount if not in callback mode
    if (!isCallback) {
      app.mount("#app");
    }
  })
  .catch((error) => {
    console.error("Failed to initialize authentication:", error);
  });
